import {RequestButtonTypes} from '../common/components/request-button/request-button.component';
import { ActivatedRoute, Router } from '@angular/router';
import {DashboardService} from '../service/events/dashboard.service';
import {RequestHandler} from '../service/OffService/request-handler';
import { Directive, ViewChild, OnDestroy } from '@angular/core';
import { environment } from '../../environments/environment';
import { Utils } from '../common/utils';

import { FormType, type Form, type FormField } from '@fitogest/reactive-form-builder/interfaces';
import { ReactiveFormBuilderComponent } from '@fitogest/reactive-form-builder';
import {
    ReactiveFormHostController,
    ReactiveFormHostRequestButtonType,
    ReactiveFormSubmitHostBridge,
    type ReactiveFormHostRequestSource,
    type ReactiveFormLoadPlan,
    type ReactiveFormLoadPolicy,
    type ReactiveFormLoadReadinessSnapshot,
} from '@fitogest/reactive-form-builder/utils';

@Directive()
export class BaseForm<T> implements OnDestroy {
    @ViewChild(ReactiveFormBuilderComponent) formBuilderRef!: ReactiveFormBuilderComponent<T>;

    public readonly hostController = new ReactiveFormHostController<T>();
    public readonly submitHostBridge = new ReactiveFormSubmitHostBridge<T>();
    public formTypes = FormType;
    public path;
    public route: ActivatedRoute;
    public router: Router;
    public dashboard: DashboardService;

    public model = {} as T;
    public modelChanges = {} as T;
    public modelOld: Record<string,string> = {};

    public flag = '';
    public request = new RequestHandler<T[]>();
    public putRequest = new RequestHandler<T[]>();
    public postRequest = new RequestHandler<T[]>();
    public undoEnabled = false;
    public requestButtonType: RequestButtonTypes = 0;

    public pathToGoBack = '';
    public headerTitleWhenEdit = '';
    public headerTitleWhenNew = '';
    public headerTitleWhenDuplicate = '';

    // SignaturePad
    public pathToFirmas = environment.serverUrl + 'firmas';
    public signaturePadOptions: object = {
        'minWidth': 1,
        'canvasWidth': 300,
        'canvasHeight': 150,
        'allowTaint': true
    };


    constructor(route: ActivatedRoute,
        router: Router,
        dashboard: DashboardService,
        putRequest: RequestHandler<T[]>,
        postRequest: RequestHandler<T[]>,
        pathToGoBack: string,
        headerTitleWhenEdit: string,
        headerTitleWhenNew: string,
        headerTitleWhenDuplicate?: string) {


        this.route = route;
        this.router = router;
        this.dashboard = dashboard;
        this.dashboard.changeClientActive = false;

        this.putRequest = putRequest;
        this.postRequest = postRequest;

        this.pathToGoBack = this.hostController.setNavigationPath(pathToGoBack);
        this.headerTitleWhenEdit = headerTitleWhenEdit;
        this.headerTitleWhenNew = headerTitleWhenNew;
        this.headerTitleWhenDuplicate = headerTitleWhenDuplicate || 'Duplicar';
        this.path = this.route.snapshot.routeConfig?.path;

    }



    public get minTimeExahusted(): boolean {
        return this.hostController.state.minTimeExhausted;
    }

    public set minTimeExahusted(value: boolean) {
        if (value) {
            this.hostController.state.markMinimumReadyTimeElapsed();
        }
    }

    public get externalDropdownsReady(): boolean {
        return this.hostController.state.externalDropdownsReady;
    }

    public get formReady(): boolean {
        return this.hostController.state.formReady;
    }

    public get repeatablesReady(): boolean {
        return this.hostController.state.repeatablesReady;
    }

    public get previousData(): T | null {
        return this.hostController.resolvePreviousData(this.model);
    }

    public set externalDropdownsReady(value: boolean) {
        if (value) {
            this.finalizeExternalDropdowns();
        } else {
            this.resetExternalDropdowns();
        }
    }

    public initializeHostModel(model: T | null | undefined): T {
        const initialModel = this.updateHostModel(model);

        this.hostController.setPreviousDataFromModel();
        return initialModel;
    }

    public updateHostModel(model: T | null | undefined): T {
        const nextModel = model ?? ({} as T);

        this.model = nextModel;
        this.modelChanges = nextModel;
        this.hostController.setModel(nextModel);
        return nextModel;
    }

    public resolveReactiveFormRecordId(
        model: T | Partial<T> | Record<string, unknown> | null | undefined = this.model as Record<string, unknown>
    ): string {
        const recordId = (model as Record<string, unknown> | null | undefined)?.['id'];

        return String(recordId ?? this.getRegisterId() ?? '');
    }


    public resetExternalDropdowns(): void {
        this.hostController.resetExternalDropdowns(this.formBuilderRef);
    }

    public finalizeExternalDropdowns(): void {
        this.hostController.finalizeExternalDropdowns(this.formBuilderRef);
    }

    public markFormReady(): void {
        this.hostController.markFormReady();
    }

    public markRepeatablesReady(): void {
        this.hostController.markRepeatablesReady();
    }

    public captureStableValueChange(change: T | { readonly value: T } | null | undefined): T {
        this.modelChanges = this.hostController.captureStableValueChange(change);
        return this.modelChanges;
    }

    public isPreviousDataChangeEvent(event: { readonly type?: string | null | undefined } | null | undefined): boolean {
        return this.hostController.isPreviousDataChangeEvent(event);
    }

    public isReadyForHydration(requireRepeatables = true): boolean {
        return this.hostController.getReadinessSnapshot({ requireRepeatables }).hydrationReady;
    }

    public createReactiveFormLoadPlan(
        form: Form<T>,
        policy?: ReactiveFormLoadPolicy | undefined,
    ): ReactiveFormLoadPlan {
        return this.hostController.createLoadPlan(form, {
            policy,
            markRecordReady: true,
        });
    }

    public getReactiveFormLoadReadiness(
        policy?: ReactiveFormLoadPolicy | undefined,
        requireRepeatables = true,
    ): ReactiveFormLoadReadinessSnapshot {
        return this.hostController.getLoadReadinessSnapshot({
            policy,
            requireRepeatables,
        });
    }

    public init(mode?: string, fieldsToDelete?: string[]) {
        if (!this.model) { (this.model as Record<string,string>) = {}; }
        if (!this.modelChanges) { (this.modelChanges as Record<string,string>) = {}; }
        if (!this.modelOld) { this.modelOld = {}; }

        this.flag = mode ?? '';

        this.route.queryParams.subscribe((params) => {
            new Promise((resolve) => {
                if (params && params['model']) {
                    this.model = JSON.parse(params['model']) || {};
                    this.hostController.setModel(this.model);
                    this.hostController.setPreviousDataFromModel();
                } else {
                    (this.model as Record<string,string>) = {};
                    this.hostController.setModel(this.model);
                    this.hostController.setPreviousDataFromModel();
                }

                resolve(0);
            })
                .then(() => {
                    if (!mode) {
                        this.flag = this.hostController.resolveModeFromModel(this.model);
                        this.hostController.setModeFromFlag(this.flag);
                    }
                    return;
                })
                .then(() => {
                    this.setUp(fieldsToDelete);
                    return;
                }).catch (e => {
                    console.log('catch en getPosition: ' + e);
                }
                );
        }).unsubscribe();
    }

    public getType(): FormType {
        return this.hostController.resolveFormTypeFromPath(this.path);
    }

    public getRegisterId(): string {
        const registerId = this.route.snapshot.paramMap.get('id') ?? this.route.snapshot.params?.['id'];

        return String(registerId ?? '');
    }

    public getModel(): T {
        return { ...(this.route.snapshot.params ?? {}) } as T;
    }

    public softInit(type: FormType) {

        this.hostController.scheduleMinimumReadyTime();

        this.flag = this.hostController.resolveModeFromFormType(type);
        this.hostController.setModeFromFormType(type);

        switch (this.flag) {
        case 'edit':
            this.request = this.putRequest;
            break;
        case 'duplicate':
        case 'new':
            this.request = this.postRequest;
            break;
        }

        this.setUp();

    }

    private resolveRequestButtonType(buttonType = this.hostController.resolveRequestButtonType()): RequestButtonTypes {
        switch (buttonType) {
        case ReactiveFormHostRequestButtonType.EDIT:
            return RequestButtonTypes.EDIT;
        case ReactiveFormHostRequestButtonType.DELETE:
            return RequestButtonTypes.DELETE;
        case ReactiveFormHostRequestButtonType.NEW:
        default:
            return RequestButtonTypes.NEW;
        }
    }

    private selectRequestHandler(requestSource: ReactiveFormHostRequestSource): void {
        switch (requestSource) {
        case 'put':
            this.request = this.putRequest;
            break;
        case 'post':
            this.request = this.postRequest;
            break;
        case 'none':
        default:
            break;
        }
    }

    public setUp(fieldsToDelete?: string[]): void {
        const setup = this.hostController.setupForm({
            flag: this.flag,
            fieldsToDelete,
            titleWhenEdit: this.headerTitleWhenEdit,
            titleWhenNew: this.headerTitleWhenNew,
            titleWhenDuplicate: this.headerTitleWhenDuplicate,
        });

        this.flag = setup.mode;
        this.model = setup.model;
        this.hostController.applyDashboardTitle({ target: this.dashboard, title: setup.title });
        this.undoEnabled = setup.undoEnabled;
        this.requestButtonType = this.resolveRequestButtonType(setup.requestButtonType);
        this.selectRequestHandler(setup.requestSource);
    }

    public scrollToTop() {
        const elements = document.getElementsByClassName('ui-dialog-content ui-widget-content');
        if (elements && elements[0]) {
            setTimeout(() => {
                if (elements[0]) {
                    elements[0].scrollTop = 0;
                }
            }, 100);
        }
    }

    public goBack() {
        const navigation = this.hostController.prepareBackNavigation({
            request: this.request,
            pathToGoBack: this.pathToGoBack,
        });

        this.router.navigate(navigation.commands);
    }

    public handleReactiveSubmitGoBack(): void {
        this.submitHostBridge.emitGoBack({ emitGoBack: () => this.goBack() });
    }

    public decimalFormat(input: number | undefined, nDecimals?: number, millar?: string, comma?: string, progressiveTo?: number) {
        if (!input) {
            return '';
        }
        return Utils.decimalFormat(input, nDecimals, millar, comma, progressiveTo);
    }

    public splitFormat(str: string, splitSeparator = ';', join = false, joinSeparator = ', '): string | string[] {
        let result: string | string[] = (str || '').split(splitSeparator);
        if (join) {
            result = result.join(joinSeparator);
        }
        return result;
    }

    public splitFromArrayFormat(array: string[], prop = 'nombre', joinSeparator = ', ') {
        return (array || []).map(it => it[+prop]).join(joinSeparator);
    }

    public getItemPosition(formFields: FormField<object>[], field: string): number {
        let position = 0;
        formFields.forEach((item, index) => {
            if (item.name === field) {
                position = index;
            }
        });
        return position;
    }

    public ngOnDestroy(): void {
        this.hostController.destroyRequestsFromHost(this);
    }

}
